/* eslint-disable @typescript-eslint/no-misused-promises */
import * as amplitudeLib from '@amplitude/analytics-browser';
import { AMPLITUDE_PUBLIC_API_KEY } from '@/constants/env';
import {
  AMPLITUDE_EVENT_PROPERTIES,
  AMPLITUDE_EVENTS,
  AMPLITUDE_USER_PROPERTIES,
} from './constants';
import { TAuthenticatedUser, TGroupUser } from '@/types/user';
import { GetBusinessApplicationStatusResponse } from '@/api/hooks/useGetBusinessValidation/type';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getLocationInfo } from '@/constants/page-mapping-ga';
import { v4 as uuidv4 } from 'uuid';

let initPromise: Promise<void>;

const AMPLITUDE_DEVICE_ID_STORAGE_KEY = 'amplitude_device_id';
let deviceId = localStorage.getItem(AMPLITUDE_DEVICE_ID_STORAGE_KEY);

const shouldEnableAmplitude = () => {
  return !!AMPLITUDE_PUBLIC_API_KEY;
};

async function ensureInitialized() {
  if (!shouldEnableAmplitude()) {
    return;
  }
  if (!initPromise) {
    initPromise = new Promise<void>((resolve) => {
      if (!AMPLITUDE_PUBLIC_API_KEY) {
        return;
      }
      amplitudeLib
        .init(AMPLITUDE_PUBLIC_API_KEY, {
          autocapture: false,
        })
        .promise.then(() => {
          if (!deviceId) {
            deviceId = uuidv4();
            localStorage.setItem(AMPLITUDE_DEVICE_ID_STORAGE_KEY, deviceId);
          }
          amplitudeLib.setDeviceId(deviceId);
          console.log('Amplitude initialized successfully');
          resolve();
        })
        .catch((error) => {
          console.error('Amplitude initialization error:', error);
          resolve();
        });
    });
  }
  return await initPromise;
}

void ensureInitialized();

const amplitude = {
  track: async <T extends AMPLITUDE_EVENTS>(
    eventName: T,
    eventProperties?: AMPLITUDE_EVENT_PROPERTIES<T>,
  ) => {
    if (shouldEnableAmplitude()) {
      try {
        await ensureInitialized();
        amplitudeLib.track(eventName, eventProperties);
      } catch (_) {}
    }
  },
  setUserId: async (userId?: string) => {
    if (shouldEnableAmplitude()) {
      try {
        await ensureInitialized();
        amplitudeLib.setUserId(userId || '');
      } catch (_) {}
    }
  },
  setUserProperties: async (userProperties: AMPLITUDE_USER_PROPERTIES) => {
    if (!userProperties || Object.keys(userProperties).length === 0) {
      return;
    }

    if (shouldEnableAmplitude()) {
      try {
        await ensureInitialized();
        const identifyEvent = new amplitudeLib.Identify();
        Object.entries(userProperties).forEach(([key, value]) => {
          identifyEvent.set(key, value);
        });
        amplitudeLib.identify(identifyEvent);
      } catch (_) {}
    }
  },
  reset: async (logout?: boolean) => {
    if (shouldEnableAmplitude()) {
      try {
        await amplitude.setUserProperties({
          role: '-',
          email: '-',
          business_id: '-',
          business_syncetera_id: '-',
          application_status: '-',
          business_name: '-',
          dba_name: '-',
        });
        amplitudeLib.flush();
        if (logout) {
          amplitudeLib.reset();
        }
      } catch (_) {}
    }
  },
};

const useAmplitudeTrack = ({
  loginUser,
  businessValidation,
}: {
  loginUser?: TAuthenticatedUser | TGroupUser;
  businessValidation?: GetBusinessApplicationStatusResponse;
}) => {
  const location = useLocation();

  useEffect(() => {
    if (['/', '/public'].includes(location.pathname)) return;

    const { page, title } = getLocationInfo(location.pathname) as {
      title: string;
      page: string;
    };

    if (title) {
      void amplitude.track(AMPLITUDE_EVENTS.PAGE_VIEW, {
        page_name: title,
        page_path: page,
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const userGroupProfile = loginUser as TGroupUser;
    if (userGroupProfile?.businessGroupId) {
      // properties for multiple businesses group profile
      void amplitude.setUserProperties({
        role: userGroupProfile?.businessGroupRole || '-',
        email: userGroupProfile?.email || '-',
        business_id: '-',
        business_syncetera_id: '-',
        application_status: '-',
        business_name: '-',
        dba_name: '-',
      });
    } else if (userGroupProfile?.id) {
      const authenticatedUser = loginUser as TAuthenticatedUser;
      const loginBusiness = authenticatedUser?.businesses?.[0];

      // properties for single business user profile
      void amplitude.setUserProperties({
        role: authenticatedUser?.roles?.join(', ') || '-',
        email: authenticatedUser?.email || '-',
        business_id: loginBusiness?.id || '-',
        business_syncetera_id: loginBusiness?.syncteraBusinessId || '-',
        business_name: loginBusiness?.businessName || '-',
        dba_name:
          loginBusiness?.businessAdditionalName
            ?.map((e) => e.additionalName)
            .join(', ') || '-',
        application_status:
          loginBusiness?.id === businessValidation?.id
            ? businessValidation?.status
            : '-',
      });
    }
  }, [loginUser, businessValidation]);
};

export { useAmplitudeTrack };

export default amplitude;
